import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Grid, Paper, Typography, Button, TextField, MenuItem, IconButton, Menu } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import BusinessCardContainer from './BusinessCardContainer';
import AppSearchBar from '../../../Components/AppSearchBar';
import routes from '../../../Utils/routes';
import nodata from "../../../Assets/Mentorship.svg"
import Images from '../../../Assets/assetHelper';
import FilterIcon from '../../../Assets/filter.svg';

interface LocationState {
    businesses: any[];
    title: string;
    searchInput?: string;
    selectedFilterType?: string;
    selectedLocation?: string; 
}

const AllBusinesses: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { businesses, title, searchInput: initialSearch, selectedFilterType: initialType, selectedLocation: initialLocation } = location.state as LocationState;

    const [searchInput, setSearchInput] = useState(initialSearch || '');
    const [selectedFilterType, setSelectedFilterType] = useState(initialType || '');
    const [selectedLocation, setSelectedLocation] = useState(initialLocation || '');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filteredBusinesses, setFilteredBusinesses] = useState(businesses);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
    const [sortBy, setSortBy] = useState('');
    const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
    const sortOptions = ['A - Z', 'Z - A', 'Recently joined'];

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 600);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const filtered = businesses.filter(business => {
            const matchesSearch = searchInput.trim() === "" ||
                business.Name.toLowerCase().includes(searchInput.trim().toLowerCase());

            const matchesType = selectedFilterType === "" ||
                business.Business_Type__c === selectedFilterType;

            const matchesLocation = selectedLocation === "" ||
                (business.Business_Address__c &&
                    business.Business_Address__c.toLowerCase().includes(selectedLocation.toLowerCase()));

            return matchesSearch && matchesType && matchesLocation;
        });

        setFilteredBusinesses(filtered);
    }, [businesses, searchInput, selectedFilterType, selectedLocation]);

    const handleBack = () => {
        navigate(routes.businessdirectory);
    };

    const handleBusinessClick = (businessId: string) => {
        const selectedBusiness = businesses.find(business => business.Id === businessId);
        if (selectedBusiness) {
            navigate(`/business directory`, {
                state: {
                    businessName: selectedBusiness.Name,
                    businessSource: 'all',
                    selectedBusinessData: selectedBusiness,
                    selectedBusinessId: businessId,
                    view: 'detail'
                }
            });
        }
    };

    const toggleFilter = () => {
        setIsFilterOpen(!isFilterOpen);
    };

    const closeFilter = () => {
        setIsFilterOpen(false);
    };

    const clearFilters = () => {
        setSelectedFilterType('');
        setSelectedLocation('');
    };

    const applyFilters = () => {
        setIsFilterOpen(false);
    };

    const handleSortButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        setSortAnchorEl(event.currentTarget);
    };

    const handleSortMenuClose = () => {
        setSortAnchorEl(null);
    };

    const handleSortSelect = (option: string) => {
        setSortBy(option);
        handleSortMenuClose();
    };

    const distinctTypes = Array.from(
        new Set(businesses.map(business => business.Business_Type__c).filter(Boolean))
    ) as string[];

    const distinctLocations = Array.from(
        new Set(businesses.map(business => business.Business_Address__c).filter(Boolean))
    ) as string[];

    let sortedBusinesses = [...filteredBusinesses];
    if (sortBy === 'A - Z') {
        sortedBusinesses.sort((a, b) => (a?.Name || '').localeCompare(b?.Name || '', undefined, { sensitivity: 'base' }));
    } else if (sortBy === 'Z - A') {
        sortedBusinesses.sort((a, b) => (b?.Name || '').localeCompare(a?.Name || '', undefined, { sensitivity: 'base' }));
    } else if (sortBy === 'Recently joined') {
        sortedBusinesses.sort((a, b) => new Date(b?.CreatedDate || 0).getTime() - new Date(a?.CreatedDate || 0).getTime());
    }

    const transformedBusinessCards = sortedBusinesses.map(business => {
        const defaultContact = {
            name: "Unknown Contact",
            image: Images.Images.NoDataTheme
        };

        let contactInfo = defaultContact;
        if (business.Business_Directories__r && business.Business_Directories__r.records.length > 0) {
            const firstContact = business.Business_Directories__r.records[0].Contact__r;
            contactInfo = {
                name: firstContact.Name || defaultContact.name,
                image: firstContact.Profile_Picture_URL__c || defaultContact.image
            };
        }

        return {
            id: business.Id,
            businessImage: business.Cover_Image_Url__c || Images.Images.NoDataTheme,
            logo: business.Logo_URL__c || Images.Images.NoDataTheme,
            category: business.Business_Type__c || "No Category",
            name: business.Name,
            location: business.Business_Address__c || "No Location",
            contactName: contactInfo.name,
            contactImage: contactInfo.image
        };
    });

    return (
        <Box sx={{
            p: { xs: 1, sm: 2 },
            minHeight: { sm: '83.5vh' },
            backgroundColor: 'white',
            borderRadius: '12px'
        }}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Box display="flex" alignItems="center" mb={3}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBack}
                            sx={{
                                color: "#373A45",
                                fontWeight: 500,
                                mr: 2
                            }}
                        >
                            {title}
                        </Button>
                    </Box>

                    <Grid container spacing={2} alignItems="center" mb={3}>
                        <Grid item xs={8} md={7}>
                            <AppSearchBar
                                localSearchInput={searchInput}
                                handleChange={(e) => setSearchInput(e.target.value)}
                                handleMicHold={() => { }}
                                handleMicRelease={() => { }}
                                openSnackbar={false}
                            />
                        </Grid>
                        <Grid item xs={4} md={5} sx={{ position: 'relative' }}>
                            <Box display="flex" gap={1} justifyContent="flex-end" alignItems="center" sx={{ flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                                <Button
                                    style={{
                                        color: "#373A45",
                                        backgroundColor: "#E8EBF3",
                                        borderRadius: 5,
                                        padding: "7px 13px"
                                    }}
                                    endIcon={<img src={FilterIcon} alt="filter" style={{ width: 24, height: 24 }} />}
                                    onClick={handleSortButtonClick}
                                >
                                    <Typography sx={{ fontWeight: 500, fontSize: '12px' }}>{sortBy ? sortBy : 'Sort by'}</Typography>
                                </Button>
                                <Menu
                                    anchorEl={sortAnchorEl}
                                    open={Boolean(sortAnchorEl)}
                                    onClose={handleSortMenuClose}
                                    PaperProps={{
                                        sx: {
                                            borderRadius: 2,
                                            minWidth: 180,
                                            boxShadow: 3,
                                            mt: 1,
                                            backgroundColor: '#FFFFFF',
                                        },
                                    }}
                                >
                                    <MenuItem onClick={() => handleSortSelect('')} selected={sortBy === ''} sx={{ fontWeight: sortBy === '' ? 600 : 400 }}>
                                        Clear
                                    </MenuItem>
                                    {sortOptions.map((option) => (
                                        <MenuItem key={option} onClick={() => handleSortSelect(option)} selected={sortBy === option} sx={{ fontWeight: sortBy === option ? 600 : 400 }}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </Menu>
                            <Button
                                style={{
                                    color: "#373A45",
                                    backgroundColor: "#E8EBF3",
                                    borderRadius: 5,
                                    padding: "7px 13px"
                                }}
                                startIcon={<FilterAltOutlinedIcon style={{ color: "#373A45", fontSize: 24 }} />}
                                onClick={toggleFilter}
                            >
                                <Typography sx={{ fontWeight: 500, fontSize: '12px' }}>Filters</Typography>
                            </Button>
                            </Box>

                            {/* Filter Modal */}
                            {isFilterOpen && (
                                <Paper
                                    style={
                                        isMobile
                                            ? {
                                                position: "absolute",
                                                right: "0",
                                                top: "calc(100% + 10px)",
                                                backgroundColor: "white",
                                                width: "21rem", // Fixed width for mobile
                                                zIndex: "999",
                                                border: "1px solid #E4E4E7",
                                                borderRadius: "12px",
                                                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
                                                overflowY: "auto",
                                                maxHeight: "400px",
                                                minHeight: '340px',
                                                padding: 0,
                                                paddingBottom: '80px',
                                            }
                                            : {
                                                position: "absolute",
                                                right: "0",
                                                top: "calc(100% + 10px)",
                                                backgroundColor: "white",
                                                width: "360px",
                                                zIndex: "999",
                                                border: "1px solid #E4E4E7",
                                                borderRadius: "12px",
                                                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
                                                overflowY: "auto",
                                                maxHeight: "400px",
                                                minHeight: '340px',
                                                padding: 0,
                                                paddingBottom: '80px',
                                            }
                                    }
                                >
                                    <Box p={3} pb={2}>
                                        <Grid container>
                                            <Grid item xs={6} style={{ display: "flex", alignItems: "center" }}>
                                                <Typography variant="h6" style={{ fontWeight: "600", fontSize: "18px" }}>
                                                    Filters
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6} style={{ display: "flex", justifyContent: "flex-end" }}>
                                                <IconButton onClick={closeFilter} size="small">
                                                    <CloseIcon />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    <Box px={3} pb={3}>
                                        <Box mb={2}>
                                            <TextField
                                                select
                                                fullWidth
                                                placeholder="Location"
                                                value={selectedLocation}
                                                onChange={(e) => setSelectedLocation(e.target.value)}
                                                variant="outlined"
                                                SelectProps={{
                                                    displayEmpty: true,
                                                    renderValue: (selected) => {
                                                        return selected ? String(selected) : "Location";
                                                    }
                                                }}
                                                InputProps={{
                                                    style: {
                                                        height: "36px",
                                                        border: "0.5px solid #B2B3B7",
                                                        borderRadius: "8px"
                                                    }
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            border: '0.5px solid #B2B3B7',
                                                            borderRadius: '8px',
                                                        },
                                                        '&:hover fieldset': {
                                                            border: '0.5px solid #B2B3B7',
                                                        },
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        height: '36px',
                                                        paddingTop: '0',
                                                        paddingBottom: '0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }
                                                }}
                                            >
                                                <MenuItem value="">
                                                    <em>All Locations</em>
                                                </MenuItem>
                                                {distinctLocations.map((location) => (
                                                    <MenuItem key={location} value={location}>
                                                        {location}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Box>

                                        <Box mb={2}>
                                            <TextField
                                                select
                                                fullWidth
                                                placeholder="Business type"
                                                value={selectedFilterType}
                                                onChange={(e) => setSelectedFilterType(e.target.value)}
                                                variant="outlined"
                                                SelectProps={{
                                                    displayEmpty: true,
                                                    renderValue: (selected) => {
                                                        return selected ? String(selected) : "Business type";
                                                    }
                                                }}
                                                InputProps={{
                                                    style: {
                                                        height: "36px",
                                                        border: "0.5px solid #B2B3B7",
                                                        borderRadius: "8px"
                                                    }
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            border: '0.5px solid #B2B3B7',
                                                            borderRadius: '8px',
                                                        },
                                                        '&:hover fieldset': {
                                                            border: '0.5px solid #B2B3B7',
                                                        },
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        height: '36px',
                                                        paddingTop: '0',
                                                        paddingBottom: '0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }
                                                }}
                                            >
                                                <MenuItem value="">
                                                    <em>All Business Types</em>
                                                </MenuItem>
                                                {distinctTypes.map((type) => (
                                                    <MenuItem key={type} value={type}>
                                                        {type}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Box>

                                        <Box
                                            mt={8}
                                            display="flex"
                                            justifyContent="space-between"
                                            style={{
                                                position: "absolute",
                                                bottom: "20px",
                                                left: "0",
                                                right: "0",
                                                padding: "0 24px"
                                            }}
                                        >
                                            <Button
                                                onClick={clearFilters}
                                                style={{
                                                    borderRadius: "8px",
                                                    color: "#111827",
                                                    backgroundColor: "white",
                                                    // padding: "8px 16px",
                                                    width: "45%",
                                                    textTransform: "none",
                                                    fontWeight: "500",
                                                    border: "1px solid #E4E4E7"
                                                }}
                                            >
                                                Reset all filters
                                            </Button>
                                            <Button
                                                onClick={applyFilters}
                                                style={{
                                                    background: "linear-gradient(90deg, #FAB400 -30.21%, #FFE090 100%)",
                                                    borderRadius: "8px",
                                                    color: "#5b4200",
                                                    // padding: "8px 16px",
                                                    width: "45%",
                                                    textTransform: "none",
                                                    fontWeight: "500"
                                                }}
                                            >
                                                Show results
                                            </Button>
                                        </Box>
                                    </Box>
                                </Paper>
                            )}
                        </Grid>
                    </Grid>

                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 1, sm: 2 },
                            borderRadius: { xs: '9px', sm: '8px' },
                            backgroundColor: 'white',
                            height: { sm: 'auto' }
                        }}
                    >
                        {transformedBusinessCards.length === 0 ? (
                            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="200px">
                                <img src={nodata} alt="No results" width={120} style={{ marginBottom: 16 }} />
                                <Typography variant="h5" color="textSecondary">
                                    No businesses found matching your filters.
                                </Typography>
                            </Box>
                        ) : (
                            <BusinessCardContainer
                                cardData={transformedBusinessCards}
                                onCardClick={handleBusinessClick}
                            />
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AllBusinesses; 
